import React from "react";
import type { ArticleContent } from "./articleContent";

export const HANGOVER_RECOVERY_ARTICLE: ArticleContent = {
  intro: (
    <p>
      Hangover recovery does not have a dependable countdown that can be
      calculated from drink count alone. This page's available calculation
      reports alcohol units from drink volume, ABV, and weekly frequency; it
      does not predict when symptoms will end or when a person is safe to drive,
      work, or operate equipment. Treat the displayed alcohol-unit arithmetic
      as a record of entered drinks, not a recovery-time result.
    </p>
  ),
  howTo: (
    <ol>
      <li>Enter the measured volume and labelled ABV for each drink type.</li>
      <li>Use the weekly-frequency field only for the number of servings in a typical week.</li>
      <li>Review the resulting units as a description of the entered alcohol, not a symptom score.</li>
      <li>Do not use this page to set a time for driving or another safety-sensitive activity.</li>
    </ol>
  ),
  formula: {
    h2: "What this page can and cannot calculate",
    body: (
      <>
        <p>
          The available arithmetic is <strong>units = volume in ml × ABV (%) ÷
          1,000</strong>, with weekly units equal to units per serving multiplied
          by the entered weekly frequency.
        </p>
        <p>
          That formula contains no recovery-time term. It does not model
          symptoms, sleep, food, hydration, medicines, health conditions, or
          individual alcohol metabolism, so it cannot produce a reliable
          personal recovery duration.
        </p>
      </>
    ),
  },
  results: {
    h2: "Interpreting a recovery question",
    h3: "An alcohol-unit total is not a recovery estimate",
    body: (
      <p>
        A units result describes the strength and frequency entered. It cannot
        tell whether symptoms are present, how severe they are, or when they
        will resolve. If you feel unwell, seek appropriate medical advice; for
        severe or rapidly worsening symptoms, contact emergency services.
      </p>
    ),
  },
  tips: (
    <p>
      If your goal is to understand the amount of alcohol represented by a
      serving, check its volume and ABV. If your question is about recovery or
      immediate safety, do not infer an answer from a weekly units total.
    </p>
  ),
  mistakes: (
    <ul>
      <li>Reading weekly alcohol units as a measure of current intoxication.</li>
      <li>Assuming that a fixed number of hours per unit guarantees sobriety or safe driving.</li>
      <li>Treating symptom duration as predictable from drink strength without personal medical context.</li>
    </ul>
  ),
  legalNote: (
    <p>
      This page does not diagnose or treat a hangover and does not certify
      fitness to drive. Alcohol effects vary; use professional advice for
      health concerns and do not rely on a calculator for safety decisions.
    </p>
  ),
};
